import { Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import theme from 'theme';
import {
  PASSWORD_STRENGTH__LOWERCASE_WARNING,
  PASSWORD_STRENGTH__MIN_LENGTH_ERROR,
  PASSWORD_STRENGTH__NUMBER_ERROR,
  PASSWORD_STRENGTH__SPECIAL_CHAR_ERROR,
  PASSWORD_STRENGTH__STRONG_PASSWORD,
  PASSWORD_STRENGTH__UPPERCASE_WARNING,
} from 'translations/constants';
import { PasswordStrengthContainer, PasswordStrengthSegmentsContainer, StrengthSegment } from './styles';

type PasswordStrengthProps = {
  password: string;
};

const PasswordStrength = ({ password }: PasswordStrengthProps) => {
  const [t] = useTranslation();
  const [message, setMessage] = useState('');
  const [color, setColor] = useState(theme.palette.secondary.light);
  const [strength, setStrength] = useState(0);

  const checkBasicStrength = useCallback(() => {
    let currentStrength = 0;
    if (password.length > 7) {
      currentStrength++;
    } else {
      setMessage(PASSWORD_STRENGTH__MIN_LENGTH_ERROR);
    }
    if (/\W/.exec(password)) {
      currentStrength++;
    } else {
      setMessage(PASSWORD_STRENGTH__SPECIAL_CHAR_ERROR);
    }
    if (/\d/.exec(password)) {
      currentStrength++;
    } else {
      setMessage(PASSWORD_STRENGTH__NUMBER_ERROR);
    }
    return currentStrength;
  }, [password]);

  const checkStrength = useCallback(() => {
    let currentStrength = checkBasicStrength();
    if (currentStrength < 3) {
      if (currentStrength === 0 && password.length === 0) {
        setColor(theme.palette.secondary.light);
      } else {
        setColor(theme.palette.error.main);
      }
      setStrength(currentStrength);
      return;
    }
    setColor(theme.palette.warning.main);
    if (/[A-Z]/.exec(password)) {
      currentStrength++;
    } else {
      setMessage(PASSWORD_STRENGTH__UPPERCASE_WARNING);
    }
    if (/[a-z]/.exec(password)) {
      currentStrength++;
    } else {
      setMessage(PASSWORD_STRENGTH__LOWERCASE_WARNING);
    }
    if (currentStrength === 5) {
      setColor(theme.palette.primary.main);
      setMessage(PASSWORD_STRENGTH__STRONG_PASSWORD);
    }
    setStrength(currentStrength);
  }, [checkBasicStrength, password]);

  useEffect(() => {
    checkStrength();
  }, [checkStrength, password]);

  const isColored = (index: number) => {
    return index < strength;
  };

  return (
    <PasswordStrengthContainer>
      <PasswordStrengthSegmentsContainer>
        {[...Array(5)].map((_, index) => (
          <StrengthSegment
            key={`strength-segment-${index.toString()}`}
            color={isColored(index) ? color : theme.palette.secondary.light}
          />
        ))}
      </PasswordStrengthSegmentsContainer>
      {password.length > 0 && (
        <Typography fontSize={12} color={color}>
          {t(message)}
        </Typography>
      )}
    </PasswordStrengthContainer>
  );
};

export default PasswordStrength;
